import {
  type ClipboardEvent,
  type DragEvent,
  type FormEvent,
  type KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { ArrowUp, ChevronDown, Circle, Mic, Paperclip } from 'lucide-react';
import { cn } from '../../lib/cn.ts';
import { Button } from '../button/button.tsx';
import { IconButton } from '../icon-button/icon-button.tsx';
import {
  Menu,
  MenuContent,
  MenuGroup,
  MenuGroupLabel,
  MenuRadioGroup,
  MenuRadioItem,
  MenuTrigger,
} from '../menu/menu.tsx';
import { Spinner } from '../spinner/spinner.tsx';
import { Textarea } from '../textarea/textarea.tsx';
import { ChatAttachmentChip, type ChatAttachmentKind } from './chat-attachments.tsx';
import { dictationClock } from './dictationClock.ts';

/** A file picked but not yet sent. */
export interface ChatComposerAttachment {
  /** Whatever the caller identifies it by; only used as a key and to remove it. */
  id: string;
  kind: ChatAttachmentKind;
  name: string;
  /** The size, already in words. */
  detail?: string;
  /** Still on its way up: it cannot be sent yet, and there is nothing to open. */
  uploading?: boolean;
}

export interface ChatComposerMicrophone {
  id: string;
  /** Already in words: a device the browser will not name is named upstream. */
  label: string;
}

/**
 * Speaking a message instead of typing it.
 *
 * The composer holds none of this: it draws the state it is handed and says
 * what was pressed. Who is holding the microphone, how long for and what comes
 * back from it are the caller's, because they are the same questions the app
 * has already answered for every other recording it makes.
 */
export interface ChatComposerDictation {
  state: 'idle' | 'recording' | 'sending';
  /** Seconds recorded so far; shown as m:ss while recording. */
  seconds: number;
  onStart: () => void;
  /** Stop and send. */
  onStop: () => void;
  /** Stop and throw the recording away. */
  onCancel: () => void;
  /**
   * The list is about to be read. Browsers hand out device names only to a
   * page they have already trusted with a device, so this is the caller's cue
   * to ask for the microphone — the list fills itself once it is granted.
   */
  onOpenOptions: () => void;
  /** What can be recorded with. Empty until the microphone has been granted. */
  microphones: ChatComposerMicrophone[];
  microphoneId: string | null;
  onChooseMicrophone: (id: string) => void;
}

export interface ChatComposerLabels {
  attach: string;
  removeAttachment: string;
  dictate?: string;
  /** On the button that ends a recording: "Stop and send". */
  dictateStop?: string;
  /** The same button while the words are on their way back. */
  dictateSending?: string;
  dictateCancel?: string;
  /** Above the list of microphones. */
  dictateMicrophone?: string;
  /** The chevron beside the microphone. */
  dictateOptions?: string;
}

export interface ChatComposerProps {
  placeholder: string;
  sendLabel: string;
  /** Called with the trimmed text. The field clears itself once it returns. */
  onSend: (text: string) => void;
  disabled?: boolean;
  /**
   * Writing is refused for as long as this stands, not held for a turn the way
   * `disabled` is. Passing it makes the composer something to read, not to
   * use — so give a reason beside it.
   */
  locked?: boolean;
  /**
   * Takes the caret whenever this value changes — the caller passes whatever
   * marks a moment someone is about to write: the chat opening, a conversation
   * being started or picked up.
   */
  focusOn?: string;
  /**
   * Words put in the field by a page, unsent: the caret lands after them, so
   * Enter sends them as they are and typing changes them first. Taken whenever
   * `id` changes — the same words offered twice are offered twice.
   */
  draft?: { text: string; id: number };
  /**
   * Bumped to rattle the field once: somebody was sent here from elsewhere on
   * the page and has to be shown where "here" is.
   */
  attention?: number;
  /**
   * Files picked for the next message. Passing this — together with `onAttach`
   * — is what puts a paperclip on the composer; a chat without it has no
   * upload at all.
   */
  attachments?: ChatComposerAttachment[];
  /** Files were picked, pasted or dropped. */
  onAttach?: (files: File[]) => void;
  onRemoveAttachment?: (id: string) => void;
  /** What the file picker offers, as an `accept` list. */
  accept?: string;
  /** Whether more files may still be added — the caller holds the cap. */
  canAttach?: boolean;
  /**
   * Passing this is what puts a microphone on the composer; a chat without it
   * is typed and nothing else.
   */
  dictation?: ChatComposerDictation;
  labels?: ChatComposerLabels;
  className?: string;
}

/**
 * How tall the field may grow before it starts scrolling instead. About five
 * lines: long enough to hold a paragraph whole, short enough that the
 * conversation above it is still the larger half of the panel.
 */
const MAX_FIELD_HEIGHT = 'max-h-30';

/**
 * Where a message is written.
 *
 * A field that grows with what is typed into it, so a message of a few
 * sentences is read back whole before it is sent. Enter sends and Shift+Enter
 * starts a line, which is the arrangement every chat has taught people to
 * expect. An empty message is not a message and does not send — but a file is
 * something said, so a message carrying one needs no words.
 *
 * Disabled, the composer holds the turn: no sending, no files by button, paste
 * or drop. One turn speaks at a time. The field itself stays writable and keeps
 * the caret, because a disabled field drops focus — on a phone that slams the
 * keyboard shut on every send and the whole screen jumps.
 *
 * A composer that can be spoken into becomes a recorder while it is listening:
 * the field and the paperclip step out of the pill and a clock takes their
 * place, because there is one thing happening and it is not typing. Whatever
 * was already written is still there afterwards — a recording is a second way
 * of saying something, never a reason to lose the first.
 *
 * Both ways out of a recording are in words. The one that sends stands in the
 * corner the send button has always been in; the one that throws the recording
 * away is a quiet line of text nowhere near it, because a cross beside a clock
 * is read as "done" by anybody in a hurry. Which microphone is recorded with
 * sits behind the chevron: a setting, not a thing to press.
 */
export function ChatComposer({
  placeholder,
  sendLabel,
  onSend,
  disabled = false,
  locked = false,
  focusOn,
  draft,
  attention,
  attachments = [],
  onAttach,
  onRemoveAttachment,
  accept,
  canAttach = true,
  dictation,
  labels,
  className,
}: ChatComposerProps) {
  const [text, setText] = useState('');
  const [dropping, setDropping] = useState(false);
  const field = useRef<HTMLTextAreaElement>(null);
  const picker = useRef<HTMLInputElement>(null);
  /*
   * The dictation as it stands, read from a listener that outlives the render
   * that started it: Escape is heard by the document, and by then the composer
   * may well be a recorder rather than the form the listener was set up in.
   */
  const latest = useRef(dictation);

  const uploads = onAttach !== undefined;
  /*
   * The dictation while it is running, or null. Held as the object rather than
   * as a flag so that everything the recording row draws is reached through
   * the one thing that proves there is a recording.
   */
  const listening =
    dictation !== undefined && dictation.state !== 'idle' ? dictation : null;
  /* Nothing may be pressed while the words are on their way to the server. */
  const transcribing = listening?.state === 'sending';
  /* A file still going up would be left behind by a send, text or not. */
  const waiting = attachments.some((attachment) => attachment.uploading);
  const canSend =
    (text.trim().length > 0 || attachments.length > 0) &&
    !waiting &&
    !disabled &&
    !locked;

  useEffect(() => {
    latest.current = dictation;
  });

  /*
   * A hidden field cannot take the caret, and asking it to is not an error —
   * the browser ignores it — so the closed drawer needs no special case.
   */
  useEffect(() => {
    field.current?.focus();
  }, [focusOn]);

  /*
   * The draft replaces what was there: a page hands over a whole question, not
   * a fragment to be worked into a sentence someone was writing. Taken during
   * the render that first sees a new id, so the field never shows the old text.
   */
  const draftId = draft?.id;
  const draftText = draft?.text;
  const [takenDraftId, setTakenDraftId] = useState(draftId);
  if (draftId !== takenDraftId) {
    setTakenDraftId(draftId);
    if (draftId !== undefined && draftId !== 0 && draftText !== undefined)
      setText(draftText);
  }

  /* The caret goes after the words, or focusing would leave it in front of them. */
  useEffect(() => {
    if (draftId === undefined || draftId === 0 || draftText === undefined) return;
    const element = field.current;
    if (!element) return;
    element.focus();
    element.setSelectionRange(draftText.length, draftText.length);
  }, [draftId, draftText]);

  /*
   * Which ask the field has already rattled for: the class stays on until the
   * run ends. Keying the form instead would remount the field and drop the
   * caret it was just handed.
   */
  const [rattledFor, setRattledFor] = useState<number | undefined>(undefined);
  const rattling = Boolean(attention) && attention !== rattledFor;

  /*
   * The caret goes back to the field the moment a reply has landed, which is
   * the moment the next thing is written.
   */
  useEffect(() => {
    if (!disabled) {
      field.current?.focus();
    }
  }, [disabled]);

  /*
   * Coming back to the window lands in the conversation, the way it does in
   * every messenger — but only when the window came back to nothing in
   * particular. Somebody who left mid-sentence in a form elsewhere on the page
   * is returned to it by the browser, and taking the caret off them would be
   * worse than not offering it at all.
   */
  useEffect(() => {
    function onWindowFocus() {
      if (!disabled && document.activeElement === document.body) {
        field.current?.focus();
      }
    }

    window.addEventListener('focus', onWindowFocus);

    return () => window.removeEventListener('focus', onWindowFocus);
  }, [disabled]);

  /*
   * Escape throws the recording away, the way it dismisses everything else on
   * the page. On the document rather than on the form: a recording takes the
   * whole composer over, and there is nothing left in it to hold the caret.
   */
  useEffect(() => {
    if (listening === null || transcribing) {
      return;
    }

    function onEscape(event: globalThis.KeyboardEvent) {
      if (event.key === 'Escape') {
        latest.current?.onCancel();
      }
    }

    document.addEventListener('keydown', onEscape);

    return () => document.removeEventListener('keydown', onEscape);
  }, [listening, transcribing]);

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!canSend) {
      return;
    }

    onSend(text.trim());
    setText('');
    /* Sending with the button moves the caret to it; put it back. */
    field.current?.focus();
  }

  /*
   * A textarea takes Enter as a line rather than as a send, so the submit is
   * asked for explicitly and the keystroke consumed. Shift+Enter is left alone,
   * and is what writes the line. Enter that confirms an IME word (Japanese,
   * Chinese) is not a send.
   */
  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      event.currentTarget.form?.requestSubmit();
    }
  }

  function take(files: FileList | null) {
    const picked = [...(files ?? [])];
    if (picked.length > 0) {
      onAttach?.(picked);
    }
  }

  /*
   * A screenshot goes to the clipboard as a file, so pasting one is the fastest
   * way there is to share it. Text pastes are left to the field.
   */
  function onPaste(event: ClipboardEvent<HTMLTextAreaElement>) {
    if (!uploads || !canAttach || disabled || locked) {
      return;
    }

    const files = [...event.clipboardData.files];
    if (files.length > 0) {
      event.preventDefault();
      onAttach?.(files);
    }
  }

  function onDrop(event: DragEvent<HTMLFormElement>) {
    if (!uploads) {
      return;
    }

    event.preventDefault();
    setDropping(false);
    if (canAttach && !disabled && !locked) {
      take(event.dataTransfer.files);
    }
  }

  function onDragOver(event: DragEvent<HTMLFormElement>) {
    if (!uploads || !event.dataTransfer.types.includes('Files')) {
      return;
    }

    event.preventDefault();
    setDropping(true);
  }

  return (
    <>
      {attachments.length > 0 ? (
        <div data-slot='chat-composer-attachments' className='flex flex-col gap-1.5 pb-2'>
          {attachments.map((attachment) => (
            <ChatAttachmentChip
              key={attachment.id}
              kind={attachment.kind}
              name={attachment.name}
              detail={attachment.detail}
              busy={attachment.uploading}
              onRemove={
                onRemoveAttachment ? () => onRemoveAttachment(attachment.id) : undefined
              }
              removeLabel={labels?.removeAttachment}
            />
          ))}
        </div>
      ) : null}

      <form
        data-slot='chat-composer'
        onSubmit={submit}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={() => setDropping(false)}
        onAnimationEnd={(event) => {
          if (event.target === event.currentTarget) setRattledFor(attention);
        }}
        className={cn(
          'flex items-center gap-2 rounded-full border border-line bg-fill-faint py-1.5 pr-1.5',
          rattling && 'motion-safe:animate-attention',
          /* No paperclip while recording, so the row starts where text does. */
          uploads && listening === null ? 'pl-1.5' : 'pl-4',
          dropping && 'border-ink bg-fill',
          className,
        )}
      >
        {listening !== null ? (
          <div
            data-slot='chat-composer-recording'
            className='flex min-w-0 flex-1 items-center gap-2.5'
          >
            {/*
              The filled red dot every device in the room uses for this, beating
              while the microphone is open — and, once the recording is on its
              way to be read, the ring that says the waiting is ours now.
            */}
            {transcribing ? (
              <Spinner className='text-ink-muted' />
            ) : (
              <Circle
                aria-hidden='true'
                className='size-2.5 shrink-0 animate-pulse fill-danger text-danger'
              />
            )}
            <span className='text-base text-ink-secondary tabular-nums'>
              {dictationClock(listening.seconds)}
            </span>
            {/*
              Quiet, and in words. A cross beside a clock reads as the way out
              of the recording to anyone in a hurry, which is how a finished
              sentence ends up in the bin instead of in the conversation.
            */}
            {transcribing ? null : (
              <Button variant='link' size='sm' onClick={listening.onCancel}>
                {labels?.dictateCancel ?? ''}
              </Button>
            )}
          </div>
        ) : (
          <>
            {uploads ? (
              <>
                <input
                  ref={picker}
                  type='file'
                  multiple
                  accept={accept}
                  className='hidden'
                  onChange={(event) => {
                    take(event.target.files);
                    /* Cleared, so picking the same file twice in a row still fires. */
                    event.target.value = '';
                  }}
                />
                <IconButton
                  aria-label={labels?.attach ?? ''}
                  size='sm'
                  disabled={!canAttach || disabled || locked}
                  onClick={() => picker.current?.click()}
                >
                  <Paperclip size={15} aria-hidden='true' />
                </IconButton>
              </>
            ) : null}

            <Textarea
              ref={field}
              variant='bare'
              rows={1}
              value={text}
              disabled={locked}
              placeholder={placeholder}
              aria-label={placeholder}
              /*
               * A message, not a form field: nothing to fill in from saved
               * passwords or addresses. The return key says "send" because
               * Enter sends.
               */
              autoComplete='off'
              enterKeyHint='send'
              autoCapitalize='sentences'
              className={cn(MAX_FIELD_HEIGHT, 'overflow-y-auto')}
              onChange={(event) => setText(event.target.value)}
              onKeyDown={onKeyDown}
              onPaste={onPaste}
            />

            {dictation !== undefined ? (
              <div className='flex shrink-0 items-center'>
                <IconButton
                  aria-label={labels?.dictate ?? ''}
                  size='sm'
                  disabled={disabled || locked}
                  onClick={dictation.onStart}
                >
                  <Mic size={15} aria-hidden='true' />
                </IconButton>

                {/*
                  Opening the menu is when the microphone is asked for: the
                  names in it exist only for a page that has been granted one,
                  so the question comes when the list is about to be read
                  rather than after something has already been said into it.
                */}
                <Menu
                  onOpenChange={(open) => {
                    if (open) {
                      dictation.onOpenOptions();
                    }
                  }}
                >
                  <MenuTrigger
                    render={
                      <IconButton
                        aria-label={labels?.dictateOptions ?? ''}
                        size='sm'
                        className='w-4'
                      />
                    }
                    disabled={disabled || locked}
                  >
                    <ChevronDown size={13} aria-hidden='true' />
                  </MenuTrigger>
                  <MenuContent side='top' align='end' className='min-w-56'>
                    <MenuGroup>
                      <MenuGroupLabel>{labels?.dictateMicrophone ?? ''}</MenuGroupLabel>
                      {/* Nothing under the title until the names arrive, which is a moment. */}
                      <MenuRadioGroup
                        value={dictation.microphoneId}
                        onValueChange={dictation.onChooseMicrophone}
                      >
                        {dictation.microphones.map((microphone) => (
                          <MenuRadioItem key={microphone.id} value={microphone.id}>
                            {microphone.label}
                          </MenuRadioItem>
                        ))}
                      </MenuRadioGroup>
                    </MenuGroup>
                  </MenuContent>
                </Menu>
              </div>
            ) : null}
          </>
        )}

        {/*
          One button in that corner, whatever the composer is doing: it ends
          what is being said and sends it. A recording has no draft to go back
          to, so stopping it is sending it — and it says so, because an arrow
          on its own leaves people looking for the send button they know.
        */}
        {listening !== null ? (
          <Button
            size='sm'
            disabled={transcribing}
            onClick={listening.onStop}
            className='shrink-0'
          >
            {transcribing ? (
              (labels?.dictateSending ?? '')
            ) : (
              <>
                <ArrowUp size={15} aria-hidden='true' />
                {labels?.dictateStop ?? sendLabel}
              </>
            )}
          </Button>
        ) : (
          <IconButton
            type='submit'
            aria-label={sendLabel}
            disabled={!canSend}
            size='sm'
            className='bg-primary text-white hover:bg-primary-hover'
          >
            <ArrowUp size={15} aria-hidden='true' />
          </IconButton>
        )}
      </form>
    </>
  );
}
